import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-grid" data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy | Wowrolla</title>
        <meta
          name="description"
          content="Comprehensive Privacy Policy for Wowrolla, Ottawa, Ontario, Canada, including PIPEDA compliance, Quebec Law 25, data hosting disclosure, cookies, and user rights."
        />
      </Helmet>

      <section className="container mx-auto px-6 pb-14 max-w-5xl">
        <h1 className="text-4xl font-black mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
          Privacy Policy
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
          Effective date: April 29, 2026 | Location: Ottawa, Ontario, Canada
        </p>

        <div className="terminal-grid p-6 space-y-5" style={{ color: 'var(--text-secondary)' }}>
          <p>
            This Privacy Policy explains how Wowrolla (Wowrolla, we, us, our) collects, uses, shares, and protects personal information
            when you use our website and services. We are a Canadian business governed by the <em>Personal Information Protection and
            Electronic Documents Act</em> (PIPEDA) and, where applicable, Quebec's <em>Act Respecting the Protection of Personal
            Information in the Private Sector</em> (Law 25 / Bill 64).
          </p>

          {/* ── Data Hosting Disclosure ──────────────────────────────── */}
          <div className="border border-yellow-500/30 bg-yellow-500/5 rounded p-4 text-sm space-y-2">
            <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>🇨🇦 Data Hosting &amp; CLOUD Act Disclosure</p>
            <p>
              Your data is stored on servers located in Canada, operated by a third-party cloud infrastructure provider.
              Our hosting provider may be incorporated in the United States, and US law enforcement may have legal access
              to this data under the US CLOUD Act, regardless of where the servers are physically located. We do not
              voluntarily share your data with any government unless legally compelled to do so.
            </p>
          </div>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>1. Scope</h2>
          <p>
            This policy applies to information collected through Wowrolla websites, forms, analytics tools, newsletters,
            account interactions, and customer support channels.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>2. Information We Collect</h2>
          <p>We collect only what we actually use (data minimisation principle):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Contact data: email address. Name and company are optional and only collected if you provide them.</li>
            <li>Usage data: pages visited, referring pages, click events, timestamps, and session context.</li>
            <li>Device and technical data: browser type, device type, IP address, and approximate geolocation.</li>
            <li>Submitted content: form responses, support messages, and tool preference data.</li>
            <li>Marketing data: subscription status, campaign interactions, and consent records (including timestamp and method).</li>
          </ul>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Tool-finder sessions store context but not personally identifiable information beyond an email address if you voluntarily
            provide one. We do not collect your name for newsletter subscriptions unless you explicitly provide it.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>3. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide, maintain, and improve our services and recommendations.</li>
            <li>Respond to support requests and communicate about product updates.</li>
            <li>Analyze site performance, detect abuse, and improve security.</li>
            <li>Send newsletters or marketing communications — only where you have given express (double opt-in) consent.</li>
            <li>Comply with legal obligations and enforce our Terms of Service.</li>
          </ul>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>4. PIPEDA — The 10 Fair Information Principles</h2>
          <p>We adhere to all 10 PIPEDA fair information principles:</p>
          <ol className="list-decimal pl-6 space-y-2 text-sm">
            <li><strong>Accountability</strong> — Wowrolla is responsible for personal information under its control. Our designated Privacy Officer is accountable for PIPEDA compliance. See contact below.</li>
            <li><strong>Identifying Purpose</strong> — We identify the purposes for collecting personal information before or at the time of collection, as described in this policy.</li>
            <li><strong>Consent</strong> — We obtain express, informed consent before collecting, using, or disclosing personal information, except where the law permits otherwise.</li>
            <li><strong>Limiting Collection</strong> — We collect only the information necessary for the identified purposes.</li>
            <li><strong>Limiting Use, Disclosure, and Retention</strong> — We use, disclose, and retain personal information only for the purposes for which it was collected, unless you consent otherwise or the law requires it.</li>
            <li><strong>Accuracy</strong> — We keep personal information as accurate, complete, and up-to-date as necessary for its purpose. Contact us to correct inaccurate information.</li>
            <li><strong>Safeguards</strong> — We protect personal information with security measures appropriate to the sensitivity of the information.</li>
            <li><strong>Openness</strong> — We make our privacy policies and practices available to you, as this document demonstrates.</li>
            <li><strong>Individual Access</strong> - Upon written request, we will inform you of the existence, use, and disclosure of your personal information, and give you access to it. Contact support@wowrolla.ca.</li>
            <li><strong>Challenging Compliance</strong> — You may challenge our compliance with PIPEDA by contacting our Privacy Officer. You may also contact the Office of the Privacy Commissioner of Canada at priv.gc.ca.</li>
          </ol>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>5. Quebec Law 25 (Loi 25)</h2>
          <p>
            If you are a Quebec resident, you have additional rights under Quebec's <em>Act Respecting the Protection of Personal
            Information in the Private Sector</em> (in force September 2023):
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li><strong>Privacy Impact Assessment (PIA)</strong>: We conduct a PIA before implementing any new personal data collection system. Data categories assessed include: tool-finder session context, subscriber email addresses, and consulting lead data.</li>
            <li><strong>Confidentiality Incident Register</strong>: We maintain an internal log of any data incidents. You may request information about incidents affecting your data.</li>
            <li><strong>Privacy Officer</strong>: See Section 14 below.</li>
            <li><strong>Right to data portability and erasure</strong>: You may request a copy of your data in a structured format, or request erasure, by contacting support@wowrolla.ca.</li>
          </ul>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>6. Cookies and Consent</h2>
          <p>
            We use a cookie consent banner on first visit. We record your consent decision (accepted / declined) with a timestamp
            in our database. Essential cookies are set without consent; analytics and marketing cookies are set only after you
            accept. You may change your preference at any time by clearing your browser cookies or contacting us.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>7. Newsletter and Marketing Consent</h2>
          <p>
            We use double opt-in for newsletter subscriptions. Implied consent is not sufficient for marketing emails. We record
            your <code>subscribedAt</code> timestamp and <code>consentMethod: "double-opt-in"</code> for every subscriber.
            You may unsubscribe at any time using the link in any email or by contacting support@wowrolla.ca.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>8. Sharing and Disclosure</h2>
          <p>We may share data with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Service providers supporting hosting (see CLOUD Act disclosure above), analytics, email delivery, and support tooling.</li>
            <li>Affiliate and attribution platforms for transparent referral tracking.</li>
            <li>Legal and regulatory authorities when required by law.</li>
            <li>Successors in a merger, acquisition, or business transfer, subject to this policy.</li>
          </ul>
          <p>We do not sell personal information for third-party advertising purposes.</p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>9. Data Breach Notification</h2>
          <p>
            PIPEDA requires that we notify the <strong>Office of the Privacy Commissioner of Canada</strong> and affected
            individuals of any breach of security safeguards that poses a real risk of significant harm to individuals. We
            maintain an internal breach response process. Notification will be issued without unreasonable delay following
            discovery of a qualifying breach.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>10. Data Retention</h2>
          <p>
            We retain personal information only as long as necessary for business, contractual, legal, and security purposes.
            Where no longer required, we delete or anonymize information. Subscriber data is deleted within 30 days of unsubscribe
            unless retention is required by law.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>11. Security</h2>
          <p>
            We use administrative, technical, and organizational safeguards proportionate to the sensitivity of your data.
            No transmission or storage system is guaranteed 100% secure.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>12. Your Rights and Choices</h2>
          <p>Depending on your location and applicable law, you may:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access personal information we hold about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion, anonymization, or restriction where applicable.</li>
            <li>Withdraw consent for any processing activity.</li>
            <li>Opt out of marketing communications at any time.</li>
            <li>Request portability of your data (Quebec residents).</li>
            <li>File a complaint with the Office of the Privacy Commissioner of Canada (priv.gc.ca) or the Commission d'accès à l'information du Québec (CAI) if you are a Quebec resident.</li>
          </ul>
          <p>To exercise any right, contact: support@wowrolla.ca.</p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>13. Children</h2>
          <p>
            Wowrolla is not directed to children under the age of 13 (or equivalent age under local law), and we do not
            knowingly collect personal information from children.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically. Material changes will be reflected by updating the effective date and,
            where appropriate, through site notice.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>15. Contact &amp; Privacy Officer</h2>
          <p>
            <strong>Privacy Officer:</strong> Wowrolla - support@wowrolla.ca<br />
            Wowrolla<br />
            Ottawa, Ontario, Canada<br />
            Email: support@wowrolla.ca
          </p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            You may also direct PIPEDA complaints to the Office of the Privacy Commissioner of Canada:{' '}
            <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="underline">priv.gc.ca</a>.
            Quebec residents may contact the Commission d'accès à l'information:{' '}
            <a href="https://www.cai.quebec.ca" target="_blank" rel="noopener noreferrer" className="underline">cai.quebec.ca</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;

